import { createContext, useContext, useEffect, useState } from 'react';
import vconst from 'utils/helpers/structs/vconst';
import { useAuth } from 'context/AuthContext';
import useSettingsParams from 'hooks/useSettingsParams';
import { safeJSONParse } from 'utils/helpers/safeParsing';
import { _localGet, _localSet } from 'utils/helpers/gimHelper';

const ObjectContext = createContext();
export const useObject = () => useContext(ObjectContext);

const deObjecttatus = [
    vconst.objectInWork,
    vconst.objectPanding,
    vconst.objectFinishedTeam,
];

export { deObjecttatus as defaultObjectStatuses };

export const defaultSaleStatuses = [
    vconst.objectInWork,
];

const SHOW_DEBTOR_STORAGE_KEY = 'objectListShowDebtor';
const SHOW_CLIENT_STORAGE_KEY = 'objectListShowClient';
const SHOW_DEBTOR_SALES_KEY = 'saleListShowDebtor';
const SHOW_CLIENT_SALES_KEY = 'saleListShowClient';

const readLocalFlag = (key) => {
    try {
        return _localGet(key) === '1';
    } catch {
        return false;
    }
};

const writeLocalFlag = (key, value) => {
    try {
        _localSet(key, value ? '1' : '0');
    } catch {
        /* ignore */
    }
};

export const ObjectProvider = ({ children, onlySale: onlySaleProp = false }) => {
    const { auth } = useAuth();
    const params = useSettingsParams();
    const onlySale = Boolean(onlySaleProp);
    const prefix = onlySale ? 'sale' : 'object';

    const filterName = `voltox-${auth.user.ID}-${prefix}-filter`;
    const filterTeamName = `voltox-${auth.user.ID}-${prefix}-filter-team`;
    const filterClientName = `voltox-${auth.user.ID}-${prefix}-filter-client`;
    const orderName = `voltox-${auth.user.ID}-${prefix}-order`;
    const descName = `voltox-${auth.user.ID}-${prefix}-desc`;

    const defaultStatuses = onlySale ? defaultSaleStatuses : deObjecttatus;

    const [showSpinner, setShowSpinner] = useState(false);
    const [listResetNonce, setListResetNonce] = useState(0);
    const [listRefreshNonce, setListRefreshNonce] = useState(0);
    const [objectsOrder, setObjectsOrder] = useState(
        safeJSONParse(sessionStorage.getItem(orderName)) || '',
    );
    const [objectsDesc, setObjectsDesc] = useState(
        safeJSONParse(sessionStorage.getItem(descName)) === true,
    );
    const [filterStatus, setFilterStatus] = useState(
        safeJSONParse(sessionStorage.getItem(filterName)) || defaultStatuses,
    );
    const [filterTeamId, setFilterTeamId] = useState(() => {
        const parsed = safeJSONParse(sessionStorage.getItem(filterTeamName));
        return parsed ? Number(parsed) || null : null;
    });
    const [filterClientId, setFilterClientId] = useState(() => {
        const parsed = safeJSONParse(sessionStorage.getItem(filterClientName));
        return parsed ? Number(parsed) || null : null;
    });
    const [showDebtorState, setShowDebtorState] = useState(() =>
        readLocalFlag(onlySale ? SHOW_DEBTOR_SALES_KEY : SHOW_DEBTOR_STORAGE_KEY),
    );
    const [showClient, setShowClientState] = useState(() =>
        readLocalFlag(onlySale ? SHOW_CLIENT_SALES_KEY : SHOW_CLIENT_STORAGE_KEY),
    );
    const showDebtor = Boolean(params.objectPaymentControl && showDebtorState);

    const setShowDebtor = (next) => {
        if (!params.objectPaymentControl) return;
        setShowDebtorState((prev) => {
            const value = typeof next === 'function' ? next(prev) : Boolean(next);
            writeLocalFlag(onlySale ? SHOW_DEBTOR_SALES_KEY : SHOW_DEBTOR_STORAGE_KEY, value);
            return value;
        });
    };

    const setShowClient = (next) => {
        setShowClientState((prev) => {
            const value = typeof next === 'function' ? next(prev) : Boolean(next);
            writeLocalFlag(onlySale ? SHOW_CLIENT_SALES_KEY : SHOW_CLIENT_STORAGE_KEY, value);
            return value;
        });
    };

    useEffect(() => {
        sessionStorage.setItem(filterName, JSON.stringify(filterStatus));
    }, [filterStatus, filterName]);

    useEffect(() => {
        sessionStorage.setItem(filterTeamName, JSON.stringify(filterTeamId || null));
    }, [filterTeamId, filterTeamName]);

    useEffect(() => {
        sessionStorage.setItem(filterClientName, JSON.stringify(filterClientId || null));
    }, [filterClientId, filterClientName]);

    useEffect(() => {
        sessionStorage.setItem(orderName, JSON.stringify(objectsOrder));
    }, [objectsOrder, orderName]);

    useEffect(() => {
        sessionStorage.setItem(descName, JSON.stringify(objectsDesc));
    }, [objectsDesc, descName]);

    const triggerListReset = () => setListResetNonce((nonce) => nonce + 1);
    const triggerListRefresh = () => setListRefreshNonce((nonce) => nonce + 1);

    const values = {
        onlySale,
        showSpinner,
        setShowSpinner,
        objectsOrder,
        setObjectsOrder,
        objectsDesc,
        setObjectsDesc,
        filterStatus,
        setFilterStatus,
        filterTeamId,
        setFilterTeamId,
        filterClientId,
        setFilterClientId,
        showDebtor,
        setShowDebtor,
        showClient,
        setShowClient,
        listResetNonce,
        triggerListReset,
        listRefreshNonce,
        triggerListRefresh,
        defaultStatuses,
    };

    return <ObjectContext.Provider value={values}>{children}</ObjectContext.Provider>;
};
